import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Card, Badge, Button, SearchBar, Divider } from '../../../shared/components/UI'
import SplitEditorLayout from '../../../shared/components/SplitEditorLayout'
import { QRCodeDisplay } from '../../../shared/lib/qrUtils'
import { dispatchApi } from '../../../shared/lib/api'

const getBatchInfo = (d) => {
  const batch = d.coneReturn?.coneHandoff?.boilingLog?.spindleOutput?.spindleLoading?.batch
  return {
    batchId: batch?.batch_id || d.id.substring(0, 8),
    shade: batch?.shade ? `${batch.shade.name} • ${batch.shade.code}` : '-',
    unit: batch?.unit?.name || '-',
    weight: batch?.final_weight_kg || 0,
    cones: batch?.cones_count || 0,
    closedAt: batch?.closed_at ? new Date(batch.closed_at).toLocaleDateString() : '-'
  }
}

export default function DispatchPage() {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('Pending')
  const [editing, setEditing] = useState(null)
  const [customerInput, setCustomerInput] = useState('')
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    dispatchApi.list({ limit: 100 })
      .then((res) => setBatches(res.data.data))
      .finally(() => setLoading(false))
  }, [])

  const visible = batches.filter((d) => {
    const info = getBatchInfo(d)
    const matchSearch = info.batchId.includes(search) ||
      (d.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
      info.shade.toLowerCase().includes(search.toLowerCase())
    const matchFilter = filter === 'All' || (filter === 'Pending' && !d.is_dispatched) || (filter === 'Dispatched' && d.is_dispatched)
    return matchSearch && matchFilter
  })

  const attachCustomer = (id) => {
    if (!customerInput.trim()) return
    dispatchApi.attachCustomer(id, { customerName: customerInput.trim() }).then((res) => {
      const updated = res.data.data
      setBatches((p) => p.map((b) => b.id === id ? { ...b, ...updated } : b))
      if (selected?.id === id) setSelected({ ...selected, ...updated })
      setEditing(null)
      setCustomerInput('')
    })
  }

  const markDispatched = (id) => {
    dispatchApi.markDispatched(id).then((res) => {
      const updated = res.data.data
      setBatches((p) => p.map((b) => b.id === id ? { ...b, ...updated } : b))
      if (selected?.id === id) setSelected({ ...selected, ...updated })
    })
  }

  const closePanel = () => {
    setSelected(null)
    setEditing(null)
    setCustomerInput('')
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading dispatch records...</div>

  const pendingCount = batches.filter((b) => !b.is_dispatched).length
  const dispatchedCount = batches.filter((b) => b.is_dispatched).length

  return (
    <SplitEditorLayout
      isOpen={!!selected}
      panelWidth="420px"
      left={
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-t-4 border-gray-100 border-t-amber-500 rounded-xl p-4">
              <p className="stat-label">Pending Dispatch</p>
              <p className="stat-val">{pendingCount}</p>
            </div>
            <div className="bg-white border border-t-4 border-gray-100 border-t-green-500 rounded-xl p-4">
              <p className="stat-label">Dispatched</p>
              <p className="stat-val">{dispatchedCount}</p>
            </div>
          </div>

          <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by batch, customer, shade..." />

          <div className="flex gap-2">
            {['All', 'Pending', 'Dispatched'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filter === f ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {f}
              </button>
            ))}
          </div>

          <Card bodyClass="!p-0">
            <table className="data-table">
              <thead>
                <tr><th>Batch</th><th>Shade</th><th>Unit</th><th>Weight</th><th>Cones</th><th>Customer</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-400 text-sm">No batches found</td></tr>}
                {visible.map((d) => {
                  const info = getBatchInfo(d)
                  return (
                    <tr key={d.id} className={`cursor-pointer ${selected?.id === d.id ? 'bg-brand-50/40' : ''}`} onClick={() => setSelected(d)}>
                      <td className="td-mono">{info.batchId}</td>
                      <td className="text-xs text-gray-600">{info.shade}</td>
                      <td className="text-xs text-gray-500">{info.unit}</td>
                      <td className="font-medium">{info.weight.toFixed(1)} KG</td>
                      <td className="text-gray-600">{info.cones}</td>
                      <td>
                        {d.customer_name
                          ? <span className="text-sm font-medium text-gray-800">{d.customer_name}</span>
                          : <span className="text-xs text-red-500 font-medium">Not assigned</span>}
                      </td>
                      <td>
                        <Badge variant={d.is_dispatched ? 'green' : d.customer_name ? 'amber' : 'red'}>
                          {d.is_dispatched ? 'Dispatched' : d.customer_name ? 'Ready' : 'Pending'}
                        </Badge>
                      </td>
                      <td><Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(d) }}>View</Button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Card>
        </div>
      }
      right={selected && (() => {
        const info = getBatchInfo(selected)
        return (
          <Card
            title={`Batch ${info.batchId}`}
            action={
              <button
                onClick={closePanel}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Close panel"
              >
                <X size={16} />
              </button>
            }
            className="sticky top-2 shadow-xl border border-brand-100"
          >
            <div className="flex justify-center mb-4">
              <QRCodeDisplay
                value={`JARITEX-BATCH-${info.batchId}-${info.shade}`}
                label={`MASTER • ${info.batchId}`}
                sublabel={info.closedAt}
                size={140}
              />
            </div>

            <div className="info-card mb-4">
              <div className="info-row"><span className="info-label">Shade</span><span className="info-value">{info.shade}</span></div>
              <div className="info-row"><span className="info-label">Unit</span><span className="info-value">{info.unit}</span></div>
              <div className="info-row"><span className="info-label">Final weight</span><span className="info-value">{info.weight.toFixed(1)} KG</span></div>
              <div className="info-row"><span className="info-label">No. of cones</span><span className="info-value">{info.cones}</span></div>
              <div className="info-row" style={{ borderBottom: 'none' }}><span className="info-label">Closed on</span><span className="info-value">{info.closedAt}</span></div>
            </div>

            <div className="mb-4">
              <label className="form-label">Customer name</label>
              {editing === selected.id ? (
                <div className="flex gap-2">
                  <input
                    className="form-input flex-1 text-sm"
                    placeholder="Enter customer name"
                    value={customerInput}
                    onChange={(e) => setCustomerInput(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && attachCustomer(selected.id)}
                  />
                  <Button size="sm" onClick={() => attachCustomer(selected.id)} disabled={!customerInput.trim()}>Save</Button>
                  <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>×</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-sm flex-1 ${selected.customer_name ? 'font-medium text-gray-800' : 'text-red-500'}`}>
                    {selected.customer_name || 'Not assigned'}
                  </span>
                  {!selected.is_dispatched && (
                    <Button variant="secondary" size="sm" onClick={() => { setEditing(selected.id); setCustomerInput(selected.customer_name || '') }}>
                      {selected.customer_name ? 'Change' : 'Assign'}
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Divider />

            {!selected.is_dispatched ? (
              <Button
                disabled={!selected.customer_name}
                onClick={() => markDispatched(selected.id)}
                className="w-full"
              >
                {selected.customer_name ? `Mark Dispatched to ${selected.customer_name}` : 'Assign customer first'}
              </Button>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                <p className="text-sm font-semibold text-green-700">Dispatched</p>
                <p className="text-xs text-green-600 mt-0.5">to {selected.customer_name}</p>
                {selected.dispatched_at && (
                  <p className="text-[10px] text-green-500 mt-1">
                    on {new Date(selected.dispatched_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </Card>
        )
      })()}
    />
  )
}
